/**
 * V79 — Gate de RE-ACEPTACIÓN legal. Se muestra (App.tsx) cuando /me dice
 * `legal.needsReacceptance`: el usuario aceptó una versión vieja de los
 * T&C/Aviso (o ninguna — p.ej. usuarios invitados pre-gate) y debe re-aceptar
 * los VIGENTES antes de seguir usando el app. No es saltable, pero NO encierra:
 * puede cerrar sesión. Incluye el opt-in de marketing OPCIONAL (V78 — misma
 * semántica y mecanismo que el checkbox del signup: no premarcado, su decisión
 * se registra en users.marketing_consent).
 */
import { useState, type FormEvent } from 'react';
import { ScrollText } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { legalPath, LEGAL_EFFECTIVE_DATE_LABEL } from './legalConfig';

const linkCls =
  'font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900';

export function LegalReacceptanceGate({
  onAccepted,
  onLogout,
}: {
  onAccepted: () => Promise<void> | void;
  onLogout: () => void;
}) {
  const [acceptedLegal, setAcceptedLegal] = useState(false); // NO premarcado
  const [marketingOptIn, setMarketingOptIn] = useState(false); // NO premarcado
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!acceptedLegal || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api('/legal/accept', {
        method: 'POST',
        body: { acceptedLegal, marketingOptIn },
      });
      await onAccepted(); // re-fetch /me → needsReacceptance=false → entra al app
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError
          ? Array.isArray(err.body?.message)
            ? err.body.message.join(' · ')
            : err.body?.message ?? err.message
          : (err as Error)?.message ?? 'No se pudo registrar la aceptación';
      setError(msg);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4 py-10 text-slate-800">
      <form
        onSubmit={onSubmit}
        className="card-soft w-full max-w-lg rounded-2xl border border-slate-200 bg-white px-6 py-8 md:px-8"
      >
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-slate-900 text-white">
            <ScrollText className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-serif text-xl tracking-tight text-slate-900">
              Actualizamos nuestros documentos legales
            </h1>
            <p className="text-xs text-slate-500">
              Para seguir usando tu cuenta necesitamos que aceptes las versiones vigentes.
            </p>
          </div>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-slate-600">
          Los{' '}
          <a href={legalPath('terminos-y-condiciones')} target="_blank" rel="noopener noreferrer" className={linkCls}>
            Términos y Condiciones
          </a>{' '}
          y el{' '}
          <a href={legalPath('aviso-de-privacidad')} target="_blank" rel="noopener noreferrer" className={linkCls}>
            Aviso de Privacidad
          </a>{' '}
          se actualizaron. Te recomendamos leerlos (se abren en una pestaña nueva);
          tu información y tu espacio de trabajo no cambian.
        </p>

        {/* §16 — entrada en vigor. Aceptar antes de esta fecha es válido (anticipado). */}
        <p className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-800">
          Esta versión entra en vigor el {LEGAL_EFFECTIVE_DATE_LABEL}.
        </p>

        {/* Aceptación OBLIGATORIA (igual que en el signup: sin ella no hay submit). */}
        <label className="mt-5 flex items-start gap-2.5 text-[12px] leading-relaxed text-slate-600">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
            checked={acceptedLegal}
            onChange={(e) => setAcceptedLegal(e.target.checked)}
            required
          />
          <span>
            <strong>Acepto los Términos y Condiciones y el Aviso de Privacidad actualizados.</strong>
          </span>
        </label>

        {/* Opt-in de marketing OPCIONAL (V78 — mismo copy/mecanismo que el signup). */}
        <label className="mt-3 flex items-start gap-2.5 text-[12px] leading-relaxed text-slate-500">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
          />
          <span>
            Quiero recibir novedades y comunicaciones (opcional). Puedes darte de
            baja cuando quieras.
          </span>
        </label>

        {error && (
          <div className="alert-over mt-4 rounded-xl px-3 py-2 text-xs">{error}</div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onLogout}
            className="text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            Cerrar sesión
          </button>
          <button
            type="submit"
            disabled={busy || !acceptedLegal}
            title={!acceptedLegal ? 'Acepta los documentos actualizados para continuar' : undefined}
            className="rounded-xl bg-slate-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Guardando…' : 'Aceptar y continuar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default LegalReacceptanceGate;
